import { Controller } from "@hotwired/stimulus";
import { loadLitepicker } from "../lib/litepicker_loader.ts";

// Range picker with a presets sidebar: a trigger button opens a panel
// containing an inline Litepicker calendar plus a hand-rolled quick-range
// list (the library has a "ranges" plugin, but it depends on a global
// `Litepicker` var rather than being a real module, so the presets here
// are wired directly via picker.setDateRange() instead).
export default class extends Controller {
  static targets = ["trigger", "panel", "calendar", "summary"];

  declare readonly triggerTarget: HTMLButtonElement;
  declare readonly panelTarget: HTMLElement;
  declare readonly calendarTarget: HTMLElement;
  declare readonly summaryTarget: HTMLElement;

  private picker?: any;

  async connect() {
    const Litepicker = await loadLitepicker();

    this.picker = new Litepicker({
      element: this.calendarTarget,
      inlineMode: true,
      parentEl: this.calendarTarget,
      format: "MMM D, YYYY",
      numberOfMonths: 2,
      numberOfColumns: 2,
      setup: (picker: any) => {
        picker.on("selected", () => this.updateSummary());
      },
    });
  }

  disconnect() {
    this.picker?.destroy();
  }

  toggle() {
    this.panelTarget.hidden ? this.open() : this.close();
  }

  open() {
    this.panelTarget.hidden = false;
    this.triggerTarget.setAttribute("aria-expanded", "true");
  }

  close() {
    this.panelTarget.hidden = true;
    this.triggerTarget.setAttribute("aria-expanded", "false");
  }

  clickOutside(event: Event) {
    if (!this.element.contains(event.target as Node)) this.close();
  }

  keydown(event: KeyboardEvent) {
    if (event.key === "Escape") this.close();
  }

  applyPreset(event: Event) {
    const button = event.currentTarget as HTMLButtonElement;
    const preset = button.dataset.preset ?? "";
    const now = new Date();
    const day = (offset: number) => new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);

    const ranges: Record<string, [Date, Date]> = {
      today: [day(0), day(0)],
      yesterday: [day(-1), day(-1)],
      last7: [day(-6), day(0)],
      last30: [day(-29), day(0)],
      thisMonth: [new Date(now.getFullYear(), now.getMonth(), 1), day(0)],
      lastMonth: [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 0)],
      thisYear: [new Date(now.getFullYear(), 0, 1), day(0)],
      lastYear: [new Date(now.getFullYear() - 1, 0, 1), new Date(now.getFullYear() - 1, 11, 31)],
      allTime: [new Date(2000, 0, 1), day(0)],
    };

    const [start, end] = ranges[preset] ?? ranges.today;
    this.picker?.setDateRange(start, end);

    this.element.querySelectorAll(".date-range-picker__preset").forEach((el) => el.classList.remove("date-range-picker__preset--active"));
    button.classList.add("date-range-picker__preset--active");

    this.updateSummary();
  }

  updateSummary() {
    const start = this.picker?.getStartDate();
    const end = this.picker?.getEndDate();

    this.summaryTarget.textContent = start && end
      ? `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`
      : "Select date range";
  }
}
